/**
 * 訂單資料的背景更新決策（純函式，方便測試）。
 *
 * 背景：LIFF 網頁收不到 push，而訂單狀態是由瓦斯行後台那側推進的（接單、出發、完成）。
 * 原本只有「訂單建立中」空狀態會輪詢，其餘畫面都只在掛載時查一次——所以客戶看著
 * 訂單列表或詳情時，狀態不會跟著變，配送追蹤也不會在完成後收掉。
 *
 * 這裡只決定「這一拍該不該查」，timer 與事件接線在 useOrderRefresh。
 */

/** 查詢間隔。狀態變化是分鐘級的事（接單、出發、送達），不需要更密。 */
export const ORDER_REFRESH_INTERVAL_MS = 30_000;

/**
 * 還會再變的狀態。終局狀態（已完成／已取消）不必再查——
 * 一直對著不會變的資料打 API，只是白花流量與電。
 */
export const LIVE_ORDER_STATUSES = ["pending", "waiting_for_pickup", "delivering"] as const;

export const isLiveOrderStatus = (status: string | null | undefined): boolean =>
  !!status && (LIVE_ORDER_STATUSES as readonly string[]).includes(status);

export interface RefreshDecisionInput {
  /** 分頁在前景嗎。背景時不查：使用者看不到，而且 LINE 裡切出去是常態 */
  visible: boolean;
  /** 已經有查詢在跑（含使用者手動觸發的）就跳過這一拍，避免重疊請求 */
  inFlight: boolean;
  /** 畫面上還有會變的訂單嗎 */
  hasLive: boolean;
}

export const shouldRefreshOrders = ({ visible, inFlight, hasLive }: RefreshDecisionInput): boolean =>
  visible && !inFlight && hasLive;

/**
 * 配送員已經按下「完成」時，order_delivery_status 的值。
 *
 * 這是一個中文顯示字串，不是列舉——後端就是這樣給的（後台比對的也是同一個字串）。
 * 比對字串很脆弱，所以只認這個明確的「已完成」值：萬一後端改了用詞，行為會退回
 * 原本的樣子（等訂單整個結掉才收），而不是誤判成已完成把追蹤藏起來。
 */
export const DELIVERY_DONE_STATUS = "已完成訂單";

/**
 * 這趟配送實際上結束了嗎。
 *
 * 和「訂單完成」是兩件事：配送員按完成只會動 order_delivery_status，
 * order_status 要等瓦斯行把訂單結掉才會變 completed，中間可能隔很久。
 * 配送員位置該在前者就停止顯示——那之後他已經在前往下一位客戶的路上了。
 */
export const isDeliveryFinished = (orderDeliveryStatus: string | null | undefined): boolean =>
  orderDeliveryStatus === DELIVERY_DONE_STATUS;
